import React, { useEffect, useState } from 'react';
import { Pen } from 'lucide-react';

interface LoadingSpinnerProps {
  step: 'CONCEPTUALIZING' | 'GENERATING_IMAGE';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ step }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (step === 'CONCEPTUALIZING') {
      // Start at 0, move to ~45% to show initial activity
      const timer = setTimeout(() => setProgress(45), 100);
      return () => clearTimeout(timer);
    } else if (step === 'GENERATING_IMAGE') {
      // Move to ~95% during generation
      setProgress(95);
    }
  }, [step]);

  return (
    <div className="flex flex-col items-center justify-center py-12 w-full max-w-md mx-auto animate-in fade-in duration-500 px-4">
      <style>{`
        @keyframes scribble {
          0% { transform: translate(0, 0) rotate(-12deg); }
          25% { transform: translate(1px, -2px) rotate(-15deg); }
          50% { transform: translate(0, -1px) rotate(-10deg); }
          75% { transform: translate(-1px, -2px) rotate(-14deg); }
          100% { transform: translate(0, 0) rotate(-12deg); }
        }
        .animate-scribble {
          animation: scribble 0.4s infinite ease-in-out;
        }
      `}</style>
      
      {/* Progress Container */}
      <div className="relative w-full h-16 flex items-center">
        {/* Background Guide Line (Dotted for sketch feel) */}
        <div className="absolute left-0 right-0 top-1/2 h-px bg-gray-300 border-b border-dashed" />

        {/* Active Ink Line */}
        <div 
            className="absolute left-0 top-1/2 h-1.5 bg-black rounded-l-full shadow-sm transition-all duration-[3000ms] ease-out -translate-y-1/2"
            style={{ width: `${progress}%` }}
        />

        {/* The Pen */}
        <div 
            className="absolute top-1/2 -translate-y-1/2 transition-all duration-[3000ms] ease-out z-10"
            style={{ left: `${progress}%` }}
        >
           {/* Icon wrapper to handle positioning relative to line tip */}
           {/* The pen tip (bottom-left of icon) needs to be at (0,0) relative to this container */}
           <div className={`relative -translate-x-[3px] -translate-y-[28px] ${progress > 0 && progress < 100 ? 'animate-scribble' : 'transform -rotate-12'}`}>
              <Pen 
                size={36} 
                className="text-gray-900 fill-black drop-shadow-xl filter" 
                strokeWidth={1.5}
              />
           </div>
        </div>
      </div>

      {/* Text Status */}
      <p className="text-xl font-hand text-gray-600 mt-4 animate-pulse">
        {step === 'CONCEPTUALIZING' ? 'Drafting the metaphor...' : 'Inking the final sketch...'}
      </p>
    </div>
  );
};

export default LoadingSpinner;